/**
 * Global DOM auto-translation engine.
 *
 * Walks every visible text node (and input placeholders) in the app, translates
 * them via the batched /api/translate pipeline (LLM + Mongo/localStorage cache),
 * and keeps up with React re-renders through a MutationObserver.
 *
 * Opt-out: add `data-notranslate` (or translate="no") to any element subtree.
 */
import { translateOne } from "./translate";

const SKIP_TAGS = new Set(["SCRIPT", "STYLE", "NOSCRIPT", "CODE", "PRE", "TEXTAREA", "KBD", "SAMP", "VAR", "svg", "SVG"]);

let lang = "en";
let observer = null;
let pending = new Set();
let pendingTimer = null;

const txtMap = new WeakMap();  // TextNode -> { src, out }
const phMap = new WeakMap();   // Element  -> { src, out }

function inSkippedSubtree(el) {
    while (el) {
        if (el.nodeType === 1) {
            if (SKIP_TAGS.has(el.tagName)) return true;
            if (el.hasAttribute && (el.hasAttribute("data-notranslate") || el.getAttribute("translate") === "no")) return true;
            if (el.isContentEditable) return true;
        }
        el = el.parentElement;
    }
    return false;
}

function isTranslatable(t) {
    if (!t || t.length < 2) return false;
    if (!/[A-Za-z]{2}/.test(t)) return false;               // needs latin prose
    if (/^(https?:\/\/|www\.)/i.test(t)) return false;       // bare URLs
    if (/^[@#/.][\w./-]*$/.test(t)) return false;            // handles, tags, paths
    if (/^[\w.+-]+@[\w-]+\.[\w.]+$/.test(t)) return false;   // emails
    return true;
}

function processText(node) {
    const cur = node.nodeValue || "";
    const info = txtMap.get(node);
    if (info && info.out === cur) return;                    // our own write / already done
    if (!node.parentElement || inSkippedSubtree(node.parentElement)) return;
    const m = cur.match(/^(\s*)([\s\S]*?)(\s*)$/);
    const core = m[2];
    if (!isTranslatable(core)) return;
    const myLang = lang;
    translateOne(core, myLang).then((tr) => {
        if (lang !== myLang || lang === "en") return;
        if (node.nodeValue !== cur) return;                  // node changed meanwhile
        if (tr === core) { txtMap.set(node, { src: core, out: cur }); return; }
        const out = m[1] + tr + m[3];
        txtMap.set(node, { src: cur, out });
        node.nodeValue = out;
    });
}

function processPlaceholder(el) {
    const cur = el.getAttribute("placeholder") || "";
    const info = phMap.get(el);
    if (info && info.out === cur) return;
    if (inSkippedSubtree(el)) return;
    if (!isTranslatable(cur.trim())) return;
    const myLang = lang;
    translateOne(cur.trim(), myLang).then((tr) => {
        if (lang !== myLang || lang === "en") return;
        if ((el.getAttribute("placeholder") || "") !== cur) return;
        if (tr === cur.trim()) { phMap.set(el, { src: cur, out: cur }); return; }
        phMap.set(el, { src: cur, out: tr });
        el.setAttribute("placeholder", tr);
    });
}

function scan(root) {
    if (!root) return;
    if (root.nodeType === Node.TEXT_NODE) { processText(root); return; }
    if (root.nodeType !== Node.ELEMENT_NODE && root.nodeType !== Node.DOCUMENT_NODE) return;
    const walker = document.createTreeWalker(root, NodeFilter.SHOW_TEXT);
    let n;
    while ((n = walker.nextNode())) processText(n);
    if (root.querySelectorAll) {
        if (root.hasAttribute && root.hasAttribute("placeholder")) processPlaceholder(root);
        root.querySelectorAll("[placeholder]").forEach(processPlaceholder);
    }
}

function restoreAll() {
    const walker = document.createTreeWalker(document.body, NodeFilter.SHOW_TEXT);
    let n;
    while ((n = walker.nextNode())) {
        const info = txtMap.get(n);
        if (info && n.nodeValue === info.out) n.nodeValue = info.src;
        txtMap.delete(n);
    }
    document.querySelectorAll("[placeholder]").forEach((el) => {
        const info = phMap.get(el);
        if (info && el.getAttribute("placeholder") === info.out) el.setAttribute("placeholder", info.src);
        phMap.delete(el);
    });
}

function flushPending() {
    pendingTimer = null;
    const batch = pending;
    pending = new Set();
    batch.forEach((node) => {
        if (!node.isConnected) return;
        scan(node);
    });
}

function queueNode(node) {
    pending.add(node);
    if (!pendingTimer) pendingTimer = setTimeout(flushPending, 250);
}

function onMutations(muts) {
    for (const m of muts) {
        if (m.type === "characterData") queueNode(m.target);
        else if (m.type === "childList") m.addedNodes.forEach(queueNode);
    }
}

export function setAutoTranslateLang(next) {
    const target = (next || "en").slice(0, 2);
    if (target === lang) return;
    if (observer) { observer.disconnect(); observer = null; }
    if (pendingTimer) { clearTimeout(pendingTimer); pendingTimer = null; pending = new Set(); }
    if (lang !== "en") restoreAll();
    lang = target;
    if (lang === "en") return;
    scan(document.body);
    observer = new MutationObserver(onMutations);
    observer.observe(document.body, { childList: true, subtree: true, characterData: true });
}
